import React from 'react'
import { DeviceFrameset } from 'react-device-frameset'
import 'react-device-frameset/styles/marvel-devices.min.css'

interface NotificationPreviewProps {
  title: string
  message: string
  imageUrl?: string | null
  titleLimit: number
  messageLimit: number
}

const NotificationPreview = ({ title, message, imageUrl, titleLimit, messageLimit }: NotificationPreviewProps): JSX.Element => {
  const truncate = (text: string, limit: number): string => {
    if (text.length <= limit) return text
    return text.substring(0, limit - 1) + '…'
  }

  const displayTitle = truncate(title || 'Notification Title', titleLimit)
  const displayMessage = truncate(message || 'Your message will appear here...', messageLimit)

  const now = new Date()
  const timeString = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  const isOverLimit = title.length > titleLimit || message.length > messageLimit

  return (
    <div className="flex flex-col items-center">
      <p className="text-xs text-gray-500 mb-3 font-medium">Live Preview</p>

      {/* Container with fixed height to account for scaled device */}
      <div className="h-[420px] overflow-hidden">
        <div className="transform scale-[0.6] origin-top">
          <DeviceFrameset device="iPhone X" color="black">
          {/* iOS Lock Screen */}
          <div
            className="w-full h-full bg-gradient-to-b from-gray-100 via-gray-200 to-gray-300 flex flex-col"
            style={{ minHeight: '100%' }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center px-8 pt-4 text-gray-800 text-sm">
              <span className="font-semibold">{timeString}</span>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                </svg>
                <svg className="w-6 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/>
                </svg>
              </div>
            </div>

            {/* Lock icon and date */}
            <div className="flex flex-col items-center mt-8 text-gray-700">
              <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-sm opacity-80">
                {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {/* Notification */}
            <div className="px-4 mt-8">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-4 shadow-2xl">
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Masjid App</span>
                      <span className="text-xs text-gray-400">now</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-gray-900 truncate">
                      {displayTitle}
                    </p>
                    <p className="text-sm text-gray-600 line-clamp-4 leading-relaxed mt-1">
                      {displayMessage}
                    </p>
                  </div>

                  {imageUrl && (
                    <div className="flex-shrink-0">
                      <img
                        src={imageUrl}
                        alt="Notification"
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom indicator */}
            <div className="mt-auto mb-2 flex justify-center">
              <div className="w-32 h-1 bg-gray-400 rounded-full" />
            </div>
          </div>
          </DeviceFrameset>
        </div>
      </div>

      {/* Truncation indicator */}
      {isOverLimit && (
        <p className="text-xs text-amber-600 text-center font-medium mt-2">
          Text is truncated as shown in preview
        </p>
      )}
    </div>
  )
}

export default NotificationPreview
